import { usarDataHora } from "@/composable/UsarDataHora/usarDataHora"
import {
  ModeloEventoEstatisticas,
  StatusEventoEstatisticas,
} from "@/store/estatisticas"
import {
  Acumulador,
  AcumuladoresPorDia,
  AcumuladoresPorSemana,
  ModeloAtividadeGraficoDiario,
  ModeloAtividadeGraficoSemanal,
} from "./MGraficoAtividadesPorPeriodo.modelos"
import { PeriodoDeExibicao } from "@/types/gerais"

const formatarStatus = (status: StatusEventoEstatisticas) => {
  const statusAtividadePlanejada = "planejado"
  if (status === statusAtividadePlanejada) {
    return "planejada"
  } else {
    return "realizada"
  }
}

const formatarData = (data: string | Date) => {
  if (typeof data === "string") {
    return converterStringParaData(data)
  }
  return data
}

const ordenarDatasPorOrdemCronologicaDeAntigoParaRecente = (
  listaDeDatas: Date[]
) => {
  const ordenacaoCrescente = (a: Date, b: Date) => a.getTime() - b.getTime()
  return [...listaDeDatas].sort(ordenacaoCrescente)
}

const removerRepeticoesDaListaDeDatas = (listaDeDatas: Date[]) => [
  ...new Set(listaDeDatas),
]

const {
  formatarDataNoPadraoTimesheet,
  formatarAcumuloDeMinutosPorExtenso,
  pegarDataInicioEFimDaSemanaComLimitesPorPeriodo,
  pegarNumeroSemanaDoAnoPorData,
  converterStringParaData,
} = usarDataHora()

export class AcumuladorDiario extends Acumulador<
  AcumuladoresPorDia,
  ModeloAtividadeGraficoDiario
> {
  inicializarAcumuladores() {
    const inicializarEstruturasDeAcumuloPorSemana = (listaDeDatas: Date[]) => {
      return listaDeDatas.reduce((acumuladoresPorDia, data) => {
        const diaDoAno = formatarDataNoPadraoTimesheet(data)

        acumuladoresPorDia[diaDoAno] = {
          dataReferencia: data,
          acumuloDeMinutosPlanejado: 0,
          acumuloDeMinutosRealizado: 0,
        }
        return acumuladoresPorDia
      }, {} as AcumuladoresPorDia)
    }

    const listaDeDatasSemRepeticao = removerRepeticoesDaListaDeDatas(
      this.listaDeDatasDoPeriodo
    )
    const listaDeDatasOrdenada =
      ordenarDatasPorOrdemCronologicaDeAntigoParaRecente(
        listaDeDatasSemRepeticao
      )
    return inicializarEstruturasDeAcumuloPorSemana(listaDeDatasOrdenada)
  }

  acumularDados(): AcumuladoresPorDia {
    const copiaDosAcumuladoresZerado = Object.assign({}, this.acumuladores)

    return this.atividades.reduce((acumulador, atividade) => {
      const diaDoAno = formatarDataNoPadraoTimesheet(atividade.dataInicial)

      const categoria =
        atividade.status === "planejada"
          ? "acumuloDeMinutosPlanejado"
          : "acumuloDeMinutosRealizado"
      if (
        acumulador[diaDoAno] &&
        typeof acumulador[diaDoAno][categoria] === "number"
      ) {
        acumulador[diaDoAno][categoria] += atividade.duracaoEmMinutos
      }

      return acumulador
    }, copiaDosAcumuladoresZerado)
  }

  montarLegendasGrafico(): string[] {
    return Object.values(this.acumuladores).map((acumulador) =>
      formatarDataNoPadraoTimesheet(acumulador.dataReferencia, false)
    )
  }

  montarListaDeAtividadesPlanejadas(): number[] {
    return Object.entries(this.acumuladores).map(
      ([, acumulador]) => acumulador.acumuloDeMinutosPlanejado
    )
  }

  montarListaDeAtividadesRealizadas(): number[] {
    return Object.entries(this.acumuladores).map(
      ([, acumulador]) => acumulador.acumuloDeMinutosRealizado
    )
  }

  formatarDados(listaDeAtividadesDaAPI: ModeloEventoEstatisticas[]) {
    const formatarAtividadeProModeloDoGrafico = (
      atividade: ModeloEventoEstatisticas
    ): ModeloAtividadeGraficoDiario => {
      return {
        duracaoEmMinutos: atividade.duracaoEmMinutos,
        dataInicial: formatarData(atividade.dataInicial),
        status: formatarStatus(atividade.status),
      }
    }

    return listaDeAtividadesDaAPI.map(formatarAtividadeProModeloDoGrafico)
  }
}

export class AcumuladorSemanal extends Acumulador<
  AcumuladoresPorSemana,
  ModeloAtividadeGraficoSemanal
> {
  inicializarAcumuladores(periodoDeExibicao: PeriodoDeExibicao) {
    const inicializarEstruturasDeAcumuloPorSemana = (listaDeDatas: Date[]) => {
      return listaDeDatas.reduce((acumulador, data) => {
        const { inicioDaSemana, fimDaSemana } =
          pegarDataInicioEFimDaSemanaComLimitesPorPeriodo(
            data,
            periodoDeExibicao
          )
        const numeroDaSemana = pegarNumeroSemanaDoAnoPorData(data)

        const semana = {
          inicioDaSemana,
          fimDaSemana,
          acumuloDeMinutosPlanejado: 0,
          acumuloDeMinutosRealizado: 0,
        }

        acumulador[numeroDaSemana] = semana

        return acumulador
      }, {} as AcumuladoresPorSemana)
    }

    const listaDeDatasSemRepeticao = removerRepeticoesDaListaDeDatas(
      this.listaDeDatasDoPeriodo
    )
    const listaDeDatasOrdenada =
      ordenarDatasPorOrdemCronologicaDeAntigoParaRecente(
        listaDeDatasSemRepeticao
      )
    return inicializarEstruturasDeAcumuloPorSemana(listaDeDatasOrdenada)
  }

  acumularDados(): AcumuladoresPorSemana {
    const copiaDosAcumuladoresZerado = Object.assign({}, this.acumuladores)

    return this.atividades.reduce((acumulador, atividade) => {
      const numeroDaSemana = pegarNumeroSemanaDoAnoPorData(
        atividade.dataInicial
      )

      const categoria =
        atividade.status === "planejada"
          ? "acumuloDeMinutosPlanejado"
          : "acumuloDeMinutosRealizado"

      const verificarAcumuladorDaSemanaExiste = () => {
        return (
          acumulador[numeroDaSemana] &&
          typeof acumulador[numeroDaSemana][categoria] === "number"
        )
      }

      if (verificarAcumuladorDaSemanaExiste()) {
        acumulador[numeroDaSemana][categoria] += atividade.duracaoEmMinutos
      }
      return acumulador
    }, copiaDosAcumuladoresZerado)
  }

  montarLegendasGrafico(): string[] {
    const montarStringDePeriodo = (dataInicial: Date, dataFinal: Date) => {
      const dataInicialFormatada = formatarDataNoPadraoTimesheet(
        dataInicial,
        false
      )
      const dataFinalFormatada = formatarDataNoPadraoTimesheet(dataFinal, false)

      if (dataInicialFormatada === dataFinalFormatada) {
        return dataInicialFormatada
      }
      return `${dataInicialFormatada} a ${dataFinalFormatada}`
    }
    return Object.entries(this.acumuladores).map(([, acumulador]) =>
      montarStringDePeriodo(acumulador.inicioDaSemana, acumulador.fimDaSemana)
    )
  }

  montarListaDeAtividadesPlanejadas(): number[] {
    return Object.entries(this.acumuladores).map(
      ([, acumulador]) => acumulador.acumuloDeMinutosPlanejado
    )
  }

  montarListaDeAtividadesRealizadas(): number[] {
    return Object.entries(this.acumuladores).map(
      ([, acumulador]) => acumulador.acumuloDeMinutosRealizado
    )
  }

  formatarDados(listaDeAtividadesDaAPI: ModeloEventoEstatisticas[]) {
    const formatarAtividadeProModeloDoGrafico = (
      atividade: ModeloEventoEstatisticas
    ): ModeloAtividadeGraficoSemanal => {
      return {
        duracaoEmMinutos: atividade.duracaoEmMinutos,
        dataInicial: formatarData(atividade.dataInicial),
        dataFinal: formatarData(atividade.dataFinal),
        status: formatarStatus(atividade.status),
      }
    }

    return listaDeAtividadesDaAPI.map(formatarAtividadeProModeloDoGrafico)
  }
}

export function processarDadosGraficoDeAtividades(
  atividadesGrafico: ModeloEventoEstatisticas[],
  periodoDeExibicao: PeriodoDeExibicao,
  tipoDePeriodo = "diario"
) {
  function obterAcumuladorDePeriodo() {
    const acumuladorSemanal = new AcumuladorSemanal(atividadesGrafico, {
      ...periodoDeExibicao,
    })
    const acumuladorDiario = new AcumuladorDiario(atividadesGrafico, {
      ...periodoDeExibicao,
    })
    const acumuladorPadrao = acumuladorDiario

    const definirAcumulador = () => {
      return (
        {
          semanal: acumuladorSemanal,
          diario: acumuladorDiario,
        }[tipoDePeriodo.toLowerCase()] || acumuladorPadrao
      )
    }

    return definirAcumulador()
  }

  const acumulador = obterAcumuladorDePeriodo()

  const listaDeLegendasDoGrafico = acumulador.montarLegendasGrafico()
  acumulador.acumularDados()
  const listaDeTotalDeMinutosRealizadasNoPeriodo =
    acumulador.montarListaDeAtividadesRealizadas()
  const listaDeTotalDeMinutosPlanejadosNoPeriodo =
    acumulador.montarListaDeAtividadesPlanejadas()

  return {
    listaDeLegendasDoGrafico,
    listaDeTotalDeMinutosRealizadasNoPeriodo,
    listaDeTotalDeMinutosPlanejadosNoPeriodo,
  }
}

export function usarUtilitariosGraficoDeAtividades() {
  const formatarLegendasTooltip = (
    legendaColuna: string | undefined,
    valorDaColuna: number
  ): string => {
    const formatarLegendaTooltip = (legenda: string | undefined) => {
      if (legenda) {
        return `${legenda}: `
      }
      return ""
    }

    const formatarDadoTooltip = (acumuloEmMinutos: number) => {
      return formatarAcumuloDeMinutosPorExtenso(acumuloEmMinutos)
    }

    const legenda = formatarLegendaTooltip(legendaColuna)
    const dado = formatarDadoTooltip(valorDaColuna)
    return `${legenda}${dado}`
  }

  function definirCoresParaGraficoDeAtividades() {
    const corAtividadesRealizadas = getComputedStyle(
      document.documentElement
    ).getPropertyValue("--color-yellow-100")
    const corAtividadesPlanejadas = getComputedStyle(
      document.documentElement
    ).getPropertyValue("--color-blue-100")

    return {
      corAtividadesRealizadas,
      corAtividadesPlanejadas,
    }
  }

  return {
    formatarLegendasTooltip,
    definirCoresParaGraficoDeAtividades,
  }
}
